// Runs in the app-owned WKWebView. Authentication and SDP exchange stay in RN.
export const MAC_VOICE_SCRIPT = String.raw`
(() => {
  const emit = event => window.webkit.messageHandlers.voice.postMessage(event);
  const pc = new RTCPeerConnection();
  let stream, channel, audio, context, meterTimer;
  let closed = false;
  const stopInput = () => {
    stream?.getTracks().forEach(track => track.stop());
    clearInterval(meterTimer);
    context?.close().catch(() => {});
    context = null;
  };
  const close = () => {
    closed = true;
    stopInput();
    channel?.close();
    pc.close();
    if (audio) { audio.pause(); audio.srcObject = null; audio.remove(); }
  };
  pc.onconnectionstatechange = () => emit({ type: 'connection', state: pc.connectionState });
  pc.ontrack = event => {
    audio ||= document.createElement('audio');
    audio.autoplay = true;
    audio.srcObject = event.streams[0] || new MediaStream([event.track]);
    document.body.appendChild(audio);
    audio.play().catch(error => emit({ type: 'error', message: 'Voice playback failed: ' + error.message }));
  };
  window.macVoice = { close, async command(command, payload) {
    if (closed) throw new Error('Voice connection closed');
    switch (command) {
      case 'microphone': {
        const acquired = await navigator.mediaDevices.getUserMedia({ audio: {
          echoCancellation: true, noiseSuppression: true, autoGainControl: true,
        }, video: false });
        if (closed) { acquired.getTracks().forEach(track => track.stop()); throw new Error('Voice connection closed'); }
        stream = acquired;
        stream.getAudioTracks().forEach(track => {
          pc.addTrack(track, stream);
          track.onended = () => { if (!closed) emit({ type: 'error', message: 'Microphone disconnected. Check your audio input and reconnect.' }); };
        });
        try {
          context = new AudioContext();
          await context.resume();
          const analyser = context.createAnalyser();
          analyser.fftSize = 256;
          context.createMediaStreamSource(stream).connect(analyser);
          const samples = new Uint8Array(analyser.fftSize);
          meterTimer = setInterval(() => {
            analyser.getByteTimeDomainData(samples);
            const power = samples.reduce((sum, sample) => sum + Math.pow((sample - 128) / 128, 2), 0);
            emit({ type: 'level', level: Math.min(1, Math.sqrt(power / samples.length) * 4) });
          }, 100);
        } catch (_) { /* Metering is optional; audio still travels over WebRTC. */ }
        return true;
      }
      case 'offer': {
        channel = pc.createDataChannel('oai-events');
        channel.onopen = () => emit({ type: 'open' });
        channel.onclose = () => emit({ type: 'close' });
        channel.onmessage = event => emit({ type: 'message', data: event.data });
        channel.onerror = () => emit({ type: 'error', message: 'Voice data connection failed.' });
        const offer = await pc.createOffer();
        return { type: offer.type, sdp: offer.sdp };
      }
      case 'local': await pc.setLocalDescription(payload); return true;
      case 'answer': await pc.setRemoteDescription(payload); return true;
      case 'send':
        if (channel?.readyState !== 'open') throw new Error('Voice data connection is not open');
        channel.send(payload.data); return true;
      case 'mute': stream?.getAudioTracks().forEach(track => { track.enabled = !payload.muted; }); return true;
      case 'stopInput': stopInput(); return true;
      default: throw new Error('Unknown voice command');
    }
  } };
})();
`;
